import { CommunityComment } from '@/types'
import { queryClient } from '../queryClient'
import { api } from '@/app'
import { useFlatlistProps } from '@/utils'
import { TypeGuards } from '@codeleap/types'
import { achievementsStore } from '@/stores'
import { PaginationResponse, QueryManager } from '@/packages'
import { useQuery } from '@tanstack/react-query'

type CommunityCommentFilters = {
  item_id?: CommunityComment['id']
}

const BASE_URL = 'community/comments/'

const create = async (data: Pick<CommunityComment, 'text' | 'item' | 'reply_to'>) => {
  const response = await api.post<CommunityComment>(`${BASE_URL}`, {
    text: data.text,
    item_id: data.item,
    reply_to: data.reply_to
  })

  return response.data
}

const list = async (limit: number, offset: number, filters?: CommunityCommentFilters) => {
  const response = await api.get<PaginationResponse<CommunityComment>>(`${BASE_URL}`, {
    params: {
      limit,
      offset,
      ...filters
    }
  })

  return response.data
}

const retrieve = async (id: CommunityComment['id']) => {
  const response = await api.get<CommunityComment>(`${BASE_URL}${id}/`)
  return response.data
}

export const communityCommentsManager = new QueryManager<CommunityComment, CommunityCommentFilters>({
  itemType: {} as CommunityComment,
  name: 'community-comment',
  queryClient: queryClient.client,

  async createItem(data) {
    const item = data.item
    const text = data.text
    const reply_to = data.reply_to

    if (TypeGuards.isNil(item) || TypeGuards.isNil(text)) {
      throw new Error('Comment must have text and an attached item')
    }

    const response = await create({
      item,
      text,
      reply_to
    })

    achievementsStore.verifyProgress(response?.achievement_progress)

    return response
  },

  listItems(limit, offset, filters) {
    return list(limit, offset, filters)
  },

  retrieveItem(id) {
    return retrieve(id)
  },

  keyExtractor(item) {
    return String(item.id)
  },

  limit: 20,

  actions: {
    async createReply(manager, data) {
      const item = data.item
      const text = data.text
      const reply_to = data.reply_to

      if (TypeGuards.isNil(item) || TypeGuards.isNil(text)) {
        throw new Error('Reply must have text and an attached item')
      }

      const new_reply = await create({ item, text, reply_to })

      const parent = await manager.getItem(data.reply_to as number)
      if (parent) {
        const replies = parent?.replies || []

        await manager.updateItems({ ...parent, replies: [...replies, new_reply] })
      }

      updateCommentRepliesQueryData(parent.id, new_reply)

      achievementsStore.verifyProgress(new_reply?.achievement_progress)

      return new_reply
    }
  }
})

export const useCommunityComments = (
  itemId?: CommunityComment['id'],
  options?: {
    enabled?: boolean
  }
) => {
  const { enabled = true } = options ?? {}

  const manager = communityCommentsManager.use({
    filter: {
      item_id: itemId
    },
    listOptions: {
      queryOptions: {
        enabled: !!itemId && enabled
      }
    }
  })

  const listProps = useFlatlistProps(manager, {
    noMoreItemsText: null
  })

  return { manager, listProps }
}

const queryKeyCommentReplies = (commentParentId: number) => ['comment-replies', commentParentId]

const updateCommentRepliesQueryData = (commentParentId: number, data: any, dataTempId?: string) => {
  const tempId = String(Math.random() * 10000)

  const queryKey = queryKeyCommentReplies(commentParentId)

  queryClient.client.setQueryData(queryKey, (old: CommunityComment[] | undefined) => {
    const previousData = old || []

    if (dataTempId) {
      const optimisticIndex = previousData.findIndex((d: any) => d?.tempId === dataTempId)

      if (optimisticIndex !== -1) {
        const updatedData = [...previousData]
        updatedData[optimisticIndex] = { ...data }
        return updatedData
      }

      return previousData
    }

    return [...previousData, { ...data, tempId }]
  })

  return dataTempId ?? tempId
}

export const useCommunityReplies = (commentParentId: number, initialReplies: CommunityComment[] = []) => {
  return useQuery({
    queryKey: queryKeyCommentReplies(commentParentId),
    refetchOnWindowFocus: false,
    enabled: !!commentParentId && !TypeGuards.isString(commentParentId),
    initialData: initialReplies,
    queryFn: async () => {
      const response = await api.get<CommunityComment[]>(`${BASE_URL}${commentParentId}/replies/`)
      return response.data
    }
  })
}
